import { Component, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Popover, PopoverModule } from 'primeng/popover';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';

import { UserPreferencesService } from '../../../core/preferences/user-preferences.service';
import { SoundNotificationService } from '../../../core/notifications/sound-notification.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

/** Communication Platform v2's Sound Notifications settings -- a small topbar popover for
 * the global enabled/volume toggle plus a per-notification-type mute list. The type labels
 * here are the same Notification.Type strings the backend's notification handlers emit
 * (NotifyOwnerOnInboundMessageHandler/NotifyAssigneeOnTicketAssignedHandler/
 * NotifyOwnerOnConversationTransferredHandler) -- there is no dedicated lookup endpoint for
 * "every notification type that exists," so this list is kept in sync by convention,
 * matching how PermissionNames.All already works the same way for permission strings. */
@Component({
  selector: 'app-sound-settings',
  standalone: true,
  imports: [FormsModule, ButtonModule, CheckboxModule, PopoverModule, SliderModule, TooltipModule, TranslatePipe],
  templateUrl: './sound-settings.component.html',
})
export class SoundSettingsComponent {
  protected readonly preferences = inject(UserPreferencesService);
  private readonly soundNotifications = inject(SoundNotificationService);

  @ViewChild('panel') panel?: Popover;

  protected readonly notificationTypes: { type: string; labelKey: string }[] = [
    { type: 'ConversationMessageReceived', labelKey: 'shared.soundSettings.type.newMessages' },
    { type: 'TicketAssigned', labelKey: 'shared.soundSettings.type.ticketAssigned' },
    { type: 'ConversationTransferred', labelKey: 'shared.soundSettings.type.conversationTransferred' },
  ];

  togglePanel(event: Event): void {
    this.panel?.toggle(event);
  }

  setEnabled(enabled: boolean): void {
    this.preferences.setSoundNotificationsEnabled(enabled);
  }

  setVolume(volume: number): void {
    this.preferences.setSoundNotificationsVolume(volume / 100);
  }

  toggleType(type: string): void {
    this.preferences.toggleSoundNotificationTypeMuted(type);
  }

  previewSound(): void {
    this.soundNotifications.playPreview();
  }
}
